import {
  LoadNewsfeedRequest,
  LoadNewsfeedResponse,
  Newsfeed,
  NewsFeedRequestType,
  UpdateNewsFeedStatusRequest,
} from '@features/admin/models/newsfeed.model';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { withReset } from '@shared/stores/features/with-reset';
import { pipe, switchMap, tap } from 'rxjs';
import { NewsFeedService } from '@features/admin/services/news-feed.service';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { IResponse } from '@shared/models/base/http-response.model';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';

export interface NewsFeedState {
  allNewsFeeds: Newsfeed[];
  bannerNewsFeeds: Newsfeed[];
  responseStatus: ResponseStatus;
  responseMessage?: string;
  displayBanner: boolean;
  total: number;
  page: number;
  size: number;
  sortBy: string;
  direction: string;
  error: any;
}

const initialState: NewsFeedState = {
  allNewsFeeds: [],
  bannerNewsFeeds: [],
  responseStatus: ResponseStatus.IDLE,
  displayBanner: true,
  total: 0,
  page: 0,
  size: 10,
  error: null,
  sortBy: '',
  direction: '',
  responseMessage: 'hello',
};

export const NewsFeedStore = signalStore(
  { providedIn: 'root', protectedState: true },
  withDevtools('newsfeed'),
  withState(initialState),
  withReset('newsfeed'),
  withComputed((store, newsFeedService = inject(NewsFeedService)) => ({
    activeBannerNews: computed(() => {
      return newsFeedService.getActiveBannerNews(store.bannerNewsFeeds());
    }),
    isLoading: computed(() => store.responseStatus() === ResponseStatus.LOADING),
  })),
  withMethods((store, newsFeedService = inject(NewsFeedService)) => ({
    toggleDisplayBanner(display: boolean) {
      updateState(store, 'newsfeed - toggleDisplayBanner', { displayBanner: display});
    },
    loadNewsFeeds: rxMethod<LoadNewsfeedRequest>(
      pipe(
        tap(() => updateState(store, 'newsfeed - load-all', { responseStatus: ResponseStatus.LOADING })),
        switchMap((request) => {
          return newsFeedService.loadNewsFeeds(request).pipe(
            tapResponse({
              next: (resp: IResponse<LoadNewsfeedResponse>) => {
                if (resp && resp.data) {
                  updateState(store, 'newsfeed - load-all success', {
                    total: resp.data.totalRecords,
                    allNewsFeeds: resp.data.newsfeeds,
                    bannerNewsFeeds: resp.data.bannerNewsFeeds,
                    responseStatus: ResponseStatus.LOADED,
                  });
                }
              },
              error: (err) => {
                console.log(err);
                updateState(store, 'newsfeed - load-all error', {
                  error: err,
                  responseStatus: ResponseStatus.ERROR,
                });
              },
            }),
          );
        }),
      ),
    ),
  })),
  withMethods((store, newsFeedService = inject(NewsFeedService)) => ({
    addNewsFeed: rxMethod<Newsfeed>(
      pipe(
        tap(() => updateState(store, 'newsfeed - add', { responseStatus: ResponseStatus.LOADED })),
        switchMap((request) => {
          return newsFeedService.addNewsfeed(request).pipe(
            tapResponse({
              next: (resp: IResponse<Newsfeed>) => {
                if (resp && resp.success) {
                  updateState(store, 'newsfeed - add success', { responseStatus: ResponseStatus.LOADED });
                  store.loadNewsFeeds({
                    newsFeedRequestType: NewsFeedRequestType.All,
                    page: store.page(),
                    size: store.size(),
                    sortBy: store.sortBy(),
                    sortDirection: store.direction(),
                  });
                }
              },
              error: (err) => {
                console.log(err);
                updateState(store, 'newsfeed - add error', { error: err });
              },
            }),
          );
        }),
      ),
    ),

    updateNewsFeed: rxMethod<UpdateNewsFeedStatusRequest>(
      pipe(
        tap(() => updateState(store, 'newsfeed - update', { responseStatus: ResponseStatus.LOADING })),
        switchMap((newsFeed) => {
          return newsFeedService.updateNewsfeed(newsFeed).pipe(
            tapResponse({
              next: (resp: IResponse<boolean>) => {
                if (resp && resp.success) {
                  updateState(store, 'newsfeed - update success', { responseStatus: ResponseStatus.LOADED });
                  // Update the newsfeed status in the list
                  store.loadNewsFeeds({
                    newsFeedRequestType: NewsFeedRequestType.All,
                    page: store.page(),
                    size: store.size(),
                    sortBy: store.sortBy(),
                    sortDirection: store.direction(),
                  });
                }
              },
              error: (err) => {
                console.log(err);
                updateState(store, 'newsfeed - update error', {
                  error: err,
                  responseStatus: ResponseStatus.ERROR,
                });
              },
            }),
          );
        }),
      ),
    ),

    deleteNewsFeed: rxMethod<string>(
      pipe(
        tap(() => updateState(store, 'newsfeed - delete', { responseStatus: ResponseStatus.LOADING })),
        switchMap((newsFeedId) => {
          return newsFeedService.deleteNewsfeed(newsFeedId).pipe(
            tapResponse({
              next: (resp: IResponse<boolean>) => {
                if (resp && resp.success) {
                  updateState(store, 'newsfeed - delete success', { responseStatus: ResponseStatus.LOADED });
                  store.loadNewsFeeds({
                    newsFeedRequestType: NewsFeedRequestType.All,
                    page: store.page(),
                    size: store.size(),
                    sortBy: store.sortBy(),
                    sortDirection: store.direction(),
                  });
                }
              },
              error: (err) => {
                console.log(err);
                updateState(store, 'newsfeed - delete error', { error: err });
              },
            }),
          );
        }),
      ),
    ),

    setPage: (event: PageEvent) => {
      updateState(store, 'newsfeed - updatePage', {
        page: event.pageIndex,
        size: event.pageSize,
      });
    },

    setSort: (sort: Sort) => {
      updateState(store, 'newsfeed - updateSort', {
        sortBy: sort.active,
        direction: sort.direction,
      });
    },
  })),
);
